/**
 * The gender question (batch-29, item R24). The organizer turns it on per event ("Ask each guest's gender") and may
 * mark it required; with it off no guest page shows it and nothing about it is sent. The answer is the one the guest
 * record has always held: 'M' or 'F' here — the guest pages offer these two and "no answer" (unless required).
 */
export type GenderQuestion = 'off' | 'optional' | 'required';
/** What a guest page is told: absent while the question is off. */
export type GenderAsk = 'optional' | 'required';
export type GenderAnswer = 'M' | 'F';

export const GENDER_CHOICES: readonly { value: GenderAnswer; label: string }[] = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];
export const GENDER_FOR_EVERYONE = 'Please choose Male or Female for everyone.';

/** The event's setting as stored, from anything: a database without the column, or a value it does not know, is "off". */
export function genderQuestion(raw: unknown): GenderQuestion {
  return raw === 'optional' || raw === 'required' ? raw : 'off';
}
/** What a guest page asks, from what it was sent: null = do not ask. */
export function genderAsk(raw: unknown): GenderAsk | null {
  const q = genderQuestion(raw);
  return q === 'off' ? null : q;
}
/** An answer the guest pages take, or null. */
export function genderAnswer(raw: unknown): GenderAnswer | null {
  return raw === 'M' || raw === 'F' ? raw : null;
}
/** The word for a stored answer, as the organizer's lists and exports show it; empty for none. */
export function genderLabel(raw: unknown): string {
  if (raw === 'M') return 'Male';
  if (raw === 'F') return 'Female';
  if (raw === 'X') return 'Prefer not to say';
  return '';
}
/**
 * The organizer's own screens (the guest panel, the guest export) show gender only when the event collects it — or, on
 * the panel, when this guest already has one stored, so an answer kept after the question was turned off can still be
 * seen and corrected.
 */
export const showsGender = (question: GenderQuestion, stored?: unknown): boolean => question !== 'off' || Boolean(genderLabel(stored));
/** The guest export's gender column: one header and one cell per guest while the event asks the question, none otherwise. */
export function genderColumn(question: GenderQuestion): { header: string[]; cell: (g: { gender?: string | null }) => string[] } {
  return question === 'off' ? { header: [], cell: () => [] } : { header: ['Gender'], cell: (g) => [genderLabel(g.gender)] };
}
/** The setting from the organizer's two controls. */
export const genderSetting = (ask: boolean, required: boolean): GenderQuestion => (!ask ? 'off' : required ? 'required' : 'optional');

/** One person of a booking as the stay page's "Your details" lists them for this question. */
interface StayPerson { id: string; lead: boolean; first_name: string | null; last_name: string | null; name_pending?: boolean | null; gender: string | null }
/** Who a gender field on the stay page is for: the person's name, or their place in the booking while the name is still to come. */
export function stayGenderName(p: StayPerson, index: number): string {
  const first = (p.first_name ?? '').trim();
  const name = p.name_pending ? first : [first, (p.last_name ?? '').trim()].filter(Boolean).join(' ');
  return name || (p.lead ? 'Main guest' : `Guest ${index + 1}`);
}
/**
 * What "Your details" sends for the gender question: only the people whose answer the guest changed, as {id, gender}
 * ('M', 'F', or null for no answer). Nothing changed = an empty list, and the save carries no `people` key at all.
 */
export function stayGenderChanges(people: readonly StayPerson[], chosen: Readonly<Record<string, string>>): Array<{ id: string; gender: GenderAnswer | null }> {
  const out: Array<{ id: string; gender: GenderAnswer | null }> = [];
  for (const p of people) {
    const next = chosen[p.id];
    if (next === undefined || next === (p.gender ?? '')) continue;
    out.push({ id: p.id, gender: genderAnswer(next) });
  }
  return out;
}
